import React, { FC, useContext } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { IChannel } from 'shared/types/channel'
import { CollectionContext } from 'shared/contexts/add-to-collections-manager'
import { moveChannel } from 'shared/api/channels'
import MessageContext from 'shared/contexts/message'
import RefreshDataContext from 'shared/contexts/refresh-data'

const reorder = (channels: IChannel[], startIndex: number, endIndex: number) => {
    channels = [...channels]
    const [removed] = channels.splice(startIndex, 1)
    channels.splice(endIndex, 0, removed)
    return channels
}

const insert = (channels: IChannel[], indexAt: number, items: IChannel[]) => {
    const result = [...channels]
    result.splice(indexAt, 0, ...items)
    return result
}

const setTargetId = (destination, combine) => {
    if (destination) {
        return destination.droppableId
    }
    if (combine) {
        return combine.draggableId
    }
}

type Props = {
    setReordering: (isReordering: boolean) => void
    setIsDragging: (isDragging: boolean) => void
    setCombineCollectionsSuccessMessage: (SuccessMessageCombineCollections) => void
}

const CollectionDnD: FC<Props> = ({ children, setReordering, setIsDragging, setCombineCollectionsSuccessMessage }) => {
    const { channels, getChannelChildren } = useContext(CollectionContext)
    const { sendMessage } = useContext(MessageContext)
    const { refreshChannel } = useContext(RefreshDataContext)

    const onDragStart = async () => {
        setIsDragging(true)
    }

    const onDragEnd = async (result: DropResult) => {
        setIsDragging(false)
        const { source, destination, combine, draggableId } = result
        // dropped outside the list
        if (!destination && !combine) {
            return
        }

        const targetId = setTargetId(destination, combine)
        const targetIndex = destination?.index || 0
        const { droppableId: sourceChannelId } = source
        const isReparenting = sourceChannelId !== targetId
        if (source.index !== targetIndex || isReparenting) {
            const destinationChannel = channels[parseInt(targetId)]
            const parentChannel = channels[parseInt(sourceChannelId)]
            setReordering(true)
            setIsDragging(false)
            let parentChildren = parentChannel.children || (await getChannelChildren(parentChannel))
            let destinationChildren = destinationChannel.children || (await getChannelChildren(destinationChannel))
            const channelToMove = parentChildren[source.index]
            const modifiedChildren = isReparenting
                ? insert(destinationChildren, targetIndex, [channelToMove])
                : reorder(parentChildren, source.index, targetIndex)
            if (isReparenting) {
                const oldParentChildren = parentChildren.filter(c => c.id !== channelToMove.id)
                refreshChannel(
                    { ...parentChannel, children: oldParentChildren, has_children: oldParentChildren.length > 0 },
                    { isReordering: true }
                )
            }
            refreshChannel(
                { ...destinationChannel, children: modifiedChildren, has_children: modifiedChildren.length > 0 },
                { isReordering: true }
            )
            try {
                await moveChannel(
                    channelToMove.id,
                    isReparenting ? destinationChannel.id : destinationChildren[targetIndex].id,
                    isReparenting ? 'first-child' : source.index < targetIndex ? 'right' : 'left'
                )
                if (isReparenting && destinationChildren.length > 0) {
                    const destinationIndex =
                        destinationChildren[targetIndex]?.id ||
                        // dropping at the end of the list will have too high an index,
                        // assuming that's what's happening here and dropping the target on the end
                        destinationChildren[destinationChildren.length - 1].id
                    await moveChannel(channelToMove.id, destinationIndex, source.index < targetIndex ? 'right' : 'left')
                }
            } catch (error) {
                console.error(`Move channel error:`, error)
                refreshChannel({ ...destinationChannel })
                if (isReparenting) {
                    refreshChannel({ ...parentChannel })
                }
                // TODO error for reparent
                sendMessage(`There was an error moving the channel :(`, 'error')
            }
            isReparenting &&
                setCombineCollectionsSuccessMessage({
                    draggableName: channelToMove.display_name,
                    targetChannelName: destinationChannel.display_name,
                    draggableId: draggableId,
                    sourceId: sourceChannelId,
                    originalIndex: source.index,
                })
            setReordering(false)
        }
    }
    return (
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
            {children}
        </DragDropContext>
    )
}
export default CollectionDnD
